import { oc } from "@orpc/contract";
import { z } from "zod";

/**
 * Query parameters of a Gently MCP OAuth 2.1 authorization request, as the
 * backend's /api/v1/mcp/oauth/authorize endpoint forwards them to the consent
 * page. Untrusted: every procedure re-validates them against the client.
 */
export const McpAuthorizationRequestSchema = z.object({
  response_type: z.string().optional(),
  client_id: z.string().min(1).max(255),
  redirect_uri: z.string().max(2048).optional(),
  code_challenge: z.string().max(128).optional(),
  code_challenge_method: z.string().max(16).optional(),
  state: z.string().max(2048).optional(),
  scope: z.string().max(1024).optional(),
  resource: z.string().max(2048).optional(),
});

export type McpAuthorizationRequest = z.infer<
  typeof McpAuthorizationRequestSchema
>;

/** Why a user can't connect at all (as opposed to needing to verify). */
export const McpAccessDenialReasonSchema = z.enum([
  "missing_permission",
  "imitating",
  /** The organization's admin hasn't enabled the MCP server. */
  "server_disabled",
]);

export type McpAccessDenialReason = z.infer<typeof McpAccessDenialReasonSchema>;

const McpClientSummarySchema = z.object({
  clientName: z.string().nullable(),
  /** Where the user will be sent back to — host, or scheme for native apps. */
  redirectTarget: z.string(),
  /** The client asked for `mcp:write`; pre-selects the "allow changes" box. */
  writeRequested: z.boolean(),
});

export const McpAuthorizationDescriptionSchema = z.discriminatedUnion(
  "status",
  [
    /** The user may approve. */
    McpClientSummarySchema.extend({
      status: z.literal("ready"),
      userName: z.string(),
      email: z.string(),
      organizationName: z.string(),
    }),
    /**
     * A user whose email wasn't verified by SSO (a password login):
     * they must enter a code mailed to `email` to approve.
     */
    McpClientSummarySchema.extend({
      status: z.literal("verify_email"),
      userName: z.string(),
      email: z.string(),
      organizationName: z.string(),
    }),
    /** The user can't use the Gently MCP server; they may only decline. */
    McpClientSummarySchema.extend({
      status: z.literal("denied"),
      reason: McpAccessDenialReasonSchema,
    }),
    /** The request is malformed but the client is known: bounce it back. */
    z.object({
      status: z.literal("redirect"),
      redirectUrl: z.string(),
    }),
    /** Unknown client or redirect URI: show the error, never redirect. */
    z.object({
      status: z.literal("invalid"),
      description: z.string(),
    }),
  ],
);

export type McpAuthorizationDescription = z.infer<
  typeof McpAuthorizationDescriptionSchema
>;

const RedirectResultSchema = z.object({ redirectUrl: z.string() });

/** The 6-digit code from the verification email. */
export const McpEmailVerificationCodeSchema = z
  .string()
  .trim()
  .regex(/^\d{6}$/, "Enter the 6-digit code");

export const McpConnectionSchema = z.object({
  id: z.uuid(),
  clientName: z.string().nullable(),
  /** Whether the connection may make changes (`mcp:write`). */
  canWrite: z.boolean(),
  verificationMethod: z.enum(["sso", "email_code"]),
  createdAt: z.coerce.date(),
  lastUsedAt: z.coerce.date().nullable(),
  expiresAt: z.coerce.date(),
});

export type McpConnection = z.infer<typeof McpConnectionSchema>;

/**
 * The consent step of the Gently MCP OAuth flow (docs/mcp/auth.md). The rest
 * of the authorization server speaks OAuth wire formats and lives outside
 * oRPC, under /api/v1/mcp/oauth.
 */
export const mcpOAuthContracts = {
  describeAuthorization: oc
    .input(McpAuthorizationRequestSchema)
    .output(McpAuthorizationDescriptionSchema)
    .route({
      method: "GET",
      summary: "Check an AI agent connection request",
      description:
        "Validates an external AI client's connection request and tells the consent screen whether the current user can approve, must verify email, is denied, or the request is invalid.",
    }),
  /** Mail a verification code to a `verify_email` user's email address. */
  sendEmailVerificationCode: oc
    .input(McpAuthorizationRequestSchema)
    .output(z.object({ email: z.string(), expiresInSeconds: z.number() }))
    .route({
      method: "POST",
      summary: "Email a connection verification code",
      description:
        "Emails a 6-digit code to a password-login user who must verify their address before approving an external AI client connection; returns the email and expiry.",
    }),
  approveAuthorization: oc
    .input(
      McpAuthorizationRequestSchema.extend({
        /** Required when the description was `verify_email`. */
        verificationCode: McpEmailVerificationCodeSchema.optional(),
        /**
         * Grant `mcp:write` too (create tasks, trigger workflows, start
         * agents). Off by default: connections are read-only.
         */
        allowWrites: z.boolean().optional(),
      }),
    )
    .output(RedirectResultSchema)
    .route({
      method: "POST",
      summary: "Approve an AI agent connection",
      description:
        "Grants an external AI client access to the app's tools as the current user, read-only unless writes are allowed; returns the redirect back to the client.",
    }),
  denyAuthorization: oc
    .input(McpAuthorizationRequestSchema)
    .output(RedirectResultSchema)
    .route({
      method: "POST",
      summary: "Decline an AI agent connection",
      description:
        "Declines an external AI client's connection request and returns the redirect that tells the client access was denied.",
    }),
  /** The current user's live AI agent connections. */
  listConnections: oc
    .input(z.object({}))
    .output(z.object({ connections: z.array(McpConnectionSchema) }))
    .route({
      method: "GET",
      summary: "List my AI agent connections",
      description:
        "Lists the current user's live external AI client connections with client name, write access, verification method, last use and expiry.",
    }),
  /** Revoke one connection, or every connection when `id` is omitted. */
  revokeConnections: oc
    .input(z.object({ id: z.uuid().optional() }))
    .output(z.object({ revoked: z.number().int().nonnegative() }))
    .route({
      method: "POST",
      summary: "Revoke AI agent connections",
      description:
        "Disconnects one of the current user's external AI client connections, or all of them when no id is given; returns how many were revoked.",
    }),
};
